import { type KeyArgsFunction } from "@apollo/client/cache/inmemory/policies"

/**
 * Custom keyArgs for listTransactions that excludes `filter.viewKey` from the
 * cache key.
 *
 * When a mutation fires, the server bumps the mutationVersion (part of viewKey).
 * The default keyArgs would treat the new viewKey as a completely different
 * cache entry, discarding all accumulated infinite-scroll pages and starting
 * from offset 0.
 *
 * By excluding viewKey from the cache key:
 * - The query still sends the real viewKey to the server (correct data returned)
 * - Follow-up pages still merge into the existing cache entry via offsetLimitPagination
 * - Page 0 refreshes replace the cached list so stale tail pages are not preserved
 */
export const listTransactionsKeyArgs: KeyArgsFunction = (args, context) => {
  if (!args) return context.fieldName

  // Exclude pagination (for infinite scroll) and filter.viewKey (for cache stability)
  const { pagination, page, filter, ...rest } = args

  let filterForKey = filter
  if (filter && typeof filter === "object") {
    const { viewKey, ...filterWithoutViewKey } = filter
    filterForKey = filterWithoutViewKey
  }

  return `${context.fieldName}:${stableStringify({ ...rest, filter: filterForKey })}`
}

/** JSON.stringify with sorted keys for deterministic cache key generation. */
function stableStringify(value: unknown): string {
  if (value === null || value === undefined) return "null"
  if (typeof value !== "object") return JSON.stringify(value)
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`
  const obj = value as Record<string, unknown>
  return `{${Object.keys(obj)
    .sort()
    .map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`)
    .join(",")}}`
}
